/**
 * L2 conversation — ChatPage · ChatThread · MessageBubble · ChatComposer · NavList.
 *
 * One family, because a conversation surface is one rhythm: the page owns the
 * header, the side list and the pinned composer; the thread owns the order of
 * the turns; a bubble owns one turn. Every colour is a token, so the family
 * follows the app's light and dark appearance without a second set of styles.
 */
import { useEffect, useState } from 'react';
import { resetButton, hitTarget } from './ui';
import { inboxGlyph } from './inboxkit';

const ROLES = new Set(['assistant', 'user']);

/** Reading size for a turn: halfway between the body and the large step. */
const TURN_SIZE = 'calc((var(--t-md) + var(--t-lg)) / 2)';

const ICONS = {
  menu: <><path d="M4 8h16" /><path d="M4 16h10" /></>,
  compose: (
    <>
      <path d="M12 5H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />
      <path d="M18.5 3.5l2 2L13 13l-3 1 1-3z" />
    </>
  ),
  plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  send: <><path d="M12 19V5" /><path d="M6 11l6-6 6 6" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></>,
  copy: <><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 0 1 2-2h9" /></>,
  retry: (
    <>
      <path d="M4 12a8 8 0 0 1 14-5.3L20 9" /><path d="M20 4v5h-5" />
      <path d="M20 12a8 8 0 0 1-14 5.3L4 15" /><path d="M4 20v-5h5" />
    </>
  ),
  check: <path d="M5 12l5 5 9-10" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  alert: <><circle cx="12" cy="12" r="9" /><path d="M12 8v5" /><path d="M12 16.5v.5" /></>,
  spark: (
    <>
      <path d="M12 3v4" /><path d="M12 17v4" /><path d="M3 12h4" /><path d="M17 12h4" />
      <path d="M6 6l2.5 2.5" /><path d="M15.5 15.5L18 18" /><path d="M18 6l-2.5 2.5" /><path d="M8.5 15.5L6 18" />
    </>
  ),
};

function Glyph({ name, size = 24, color = 'currentColor', weight = 2 }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth={weight} strokeLinecap="round" strokeLinejoin="round" aria-hidden
      style={{ flex: '0 0 auto' }}
    >
      {ICONS[name] || null}
    </svg>
  );
}

function IconButton({ name, label, onClick, size = 24, tone = 'var(--text)', box = 44 }) {
  return (
    <button
      type="button" aria-label={label} onClick={onClick}
      style={{
        ...resetButton, width: box, height: box, flex: '0 0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: tone,
      }}
    >
      <Glyph name={name} size={size} />
    </button>
  );
}

/** The turn's text: `props.value`, else the node's own bind. Lists join by line. */
function textOf(value, data) {
  const raw = value !== undefined ? value : data;
  if (raw == null) return '';
  if (Array.isArray(raw)) return raw.filter(Boolean).join('\n');
  return typeof raw === 'object' ? '' : String(raw);
}

function actionsOf(source) {
  if (!Array.isArray(source)) return [];
  return source.filter((a) => a && typeof a === 'object' && String(a.label || '').trim() && a.key != null);
}

/**
 * MessageBubble — one turn of a conversation.
 *
 * The user's turn is a soft bubble on the trailing edge; a reply is plain text
 * across the full width, because it is the page's own voice. `status` is the
 * delivery state: `sending` dims, `failed` keeps the turn on screen as its own
 * retry target, `streaming` draws the cursor after the words so far.
 */
export function MessageBubble({ variant, props = {}, data, emit }) {
  const text = textOf(props.value, data).trim();
  const streaming = props.status === 'streaming';
  if (!text && !streaming) return null;

  const role = ROLES.has(props.role) ? props.role : (ROLES.has(variant) ? variant : 'assistant');
  const mine = role === 'user';
  const sending = props.status === 'sending';
  const failed = props.status === 'failed';
  const tag = props.tag ? String(props.tag) : '';
  const note = props.note ? String(props.note) : '';
  const meta = props.meta ? String(props.meta) : '';
  const actions = actionsOf(props.actions);
  const tools = props.tools === true && !mine && !streaming;
  const retry = failed && emit ? () => emit('retryRequested', {}) : undefined;

  const plate = mine
    ? {
      background: failed ? 'var(--bad-soft)' : 'var(--surface-3)',
      border: failed ? '1px solid color-mix(in srgb, var(--bad) 35%, transparent)' : '1px solid transparent',
      padding: '10px var(--sp-4)',
      borderRadius: '22px 22px 6px 22px',
      maxWidth: '82%',
    }
    : { maxWidth: '100%' };

  const body = (
    <div
      data-role={role}
      data-status={props.status || 'delivered'}
      style={{
        fontSize: TURN_SIZE, lineHeight: 1.5, color: 'var(--text)',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', minWidth: 0,
        opacity: sending ? 0.55 : 1,
        ...plate,
      }}
    >
      {text}
      {streaming ? (
        <span data-cursor aria-hidden style={{
          display: 'inline-block', width: 9, height: '1.1em', marginLeft: text ? 3 : 0,
          borderRadius: 2, background: 'var(--text)', verticalAlign: 'text-bottom',
        }} />
      ) : null}
    </div>
  );

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)',
      alignItems: mine ? 'flex-end' : 'flex-start', minWidth: 0,
    }}>
      {tag ? (
        <span data-tag style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 26, padding: '0 10px',
          borderRadius: 'var(--r-pill)', background: 'var(--surface-3)',
          fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--text-2)',
        }}>
          <Glyph name="clock" size={14} weight={2.2} />
          {tag}
        </span>
      ) : null}
      {note ? (
        <span data-note style={{
          display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)',
          fontSize: 'var(--t-sm)', color: 'var(--text-2)',
        }}>
          <Glyph name="check" size={16} />
          {note}
        </span>
      ) : null}
      {retry ? (
        <button
          type="button" onClick={retry}
          style={{
            ...resetButton, ...hitTarget,
            display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start',
            maxWidth: '100%',
          }}
        >
          {body}
        </button>
      ) : body}
      {actions.length ? (
        <div data-actions style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
          {actions.map((a) => (
            <button
              key={String(a.key)} type="button"
              onClick={emit ? () => emit('actionPicked', { key: a.key, label: String(a.label) }) : undefined}
              style={{
                ...resetButton, height: 44, padding: '0 20px', borderRadius: 'var(--r-pill)',
                display: 'flex', alignItems: 'center',
                fontSize: TURN_SIZE, fontWeight: 600,
                background: a.primary ? 'var(--text)' : 'var(--surface)',
                color: a.primary ? 'var(--surface)' : 'var(--text)',
                border: a.primary ? '1px solid transparent' : '1px solid var(--line)',
              }}
            >
              {String(a.label)}
            </button>
          ))}
        </div>
      ) : null}
      {tools ? (
        <div data-tools style={{ display: 'flex', gap: 'var(--sp-1)', marginLeft: -10 }}>
          <IconButton
            name="copy" label="Copy" size={18} box={40} tone="var(--text-2)"
            onClick={emit ? () => emit('copyRequested', { text }) : undefined}
          />
          <IconButton
            name="retry" label="Try again" size={18} box={40} tone="var(--text-2)"
            onClick={emit ? () => emit('regenerateRequested', {}) : undefined}
          />
        </div>
      ) : null}
      {failed ? (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--bad)',
        }}>
          <Glyph name="alert" size={14} weight={2.2} />
          Not sent. Tap to retry.
        </span>
      ) : null}
      {meta && !failed ? (
        <span style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>{meta}</span>
      ) : null}
    </div>
  );
}

/** A thread's rows: `props.messages`, else the node's own bind. Textless rows are skipped. */
function turnsOf(source, data) {
  const raw = source !== undefined ? source : data;
  if (!Array.isArray(raw)) return [];
  return raw.filter((row) => row && typeof row === 'object'
    && (textOf(row.text).trim() || row.status === 'streaming'));
}

/** Per row: its day label, and whether it differs from the last row that had one. */
function daySplits(turns) {
  let last = null;
  return turns.map((row) => {
    const day = row.day ? String(row.day) : null;
    const split = Boolean(day) && day !== last;
    if (day) last = day;
    return { day, split };
  });
}

function suggestionsOf(source) {
  if (!Array.isArray(source)) return [];
  return source
    .map((s) => (typeof s === 'string' ? { label: s, prompt: s } : s))
    .filter((s) => s && typeof s === 'object' && String(s.label || '').trim());
}

/**
 * ChatThread — a conversation, oldest turn first.
 *
 * It owns the order and the rhythm (day separators, the typing mark, the
 * first-run surface) and delegates every turn to `MessageBubble`. A turn's
 * signals are re-emitted with the row's id, so a page wires one edge for the
 * whole thread.
 */
export function ChatThread({ props = {}, data, emit }) {
  const turns = turnsOf(props.messages, data);
  const typing = props.typing === true;

  if (!turns.length && !typing) {
    const title = props.emptyTitle ? String(props.emptyTitle) : '';
    const body = props.emptyBody ? String(props.emptyBody) : '';
    const suggestions = suggestionsOf(props.suggestions);
    if (!title && !body && !suggestions.length) return null;
    return (
      <div data-thread="empty" style={{
        display: 'flex', flexDirection: 'column', gap: 28, minWidth: 0,
        flex: '1 1 auto', justifyContent: 'center',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <span style={{
            width: 44, height: 44, borderRadius: 14, background: 'var(--text)', color: 'var(--surface)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Glyph name="spark" size={22} />
          </span>
          {title ? (
            <div style={{
              fontSize: 30, fontWeight: 700, color: 'var(--text)',
              letterSpacing: '-0.025em', lineHeight: 1.15,
            }}>{title}</div>
          ) : null}
          {body ? (
            <div style={{ fontSize: TURN_SIZE, color: 'var(--text-2)', lineHeight: 1.5 }}>{body}</div>
          ) : null}
        </div>
        {suggestions.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {suggestions.map((s, i) => (
              <button
                key={`${s.label}-${i}`}
                type="button"
                onClick={emit
                  ? () => emit('suggestionPicked', { prompt: String(s.prompt || s.label), label: String(s.label) })
                  : undefined}
                style={{
                  ...resetButton, ...hitTarget,
                  display: 'flex', flexDirection: 'column', gap: 2,
                  padding: '14px var(--sp-4)', borderRadius: 18,
                  background: 'var(--surface-3)', color: 'var(--text)',
                }}
              >
                <span style={{ fontSize: TURN_SIZE, fontWeight: 600 }}>{String(s.label)}</span>
                {s.desc ? (
                  <span style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>{String(s.desc)}</span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  const days = daySplits(turns);
  return (
    <div data-thread="turns" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', minWidth: 0 }}>
      {turns.map((row, i) => {
        const { day, split } = days[i];
        const id = row.id ?? row.item_id ?? null;
        return (
          <div key={id ?? `turn-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', minWidth: 0 }}>
            {split ? (
              <div data-day={day} style={{
                alignSelf: 'center', fontSize: 'var(--t-sm)', fontWeight: 500, color: 'var(--text-2)',
              }}>{day}</div>
            ) : null}
            <MessageBubble
              props={{
                value: row.text, role: row.role, tag: row.tag, note: row.note, meta: row.meta,
                status: row.status, actions: row.actions, tools: row.tools,
              }}
              emit={emit ? (signal, payload) => emit(signal, { ...(payload || {}), id }) : undefined}
            />
          </div>
        );
      })}
      {typing ? (
        <div data-thread="typing" aria-label="Replying" style={{
          alignSelf: 'flex-start', display: 'flex', gap: 'var(--sp-1)',
          padding: 'var(--sp-2) var(--sp-3)', borderRadius: 'var(--r-pill)', background: 'var(--surface-3)',
        }}>
          {[0, 1, 2].map((n) => (
            <span key={n} style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--text-2)' }} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * ChatComposer — the message field with its two doors.
 *
 * Send is live only when there is something to send. While a reply is on its
 * way (`busy`) the same button is Stop; the field stays editable, so the next
 * message can be written before this one finishes.
 */
export function ChatComposer({ props = {}, ctx, emit, slots }) {
  const [text, setText] = useState(props.seed ? String(props.seed) : '');
  const pushed = slots?.setText;
  useEffect(() => {
    if (!pushed) return;
    const next = pushed?.payload?.text ?? pushed?.text;
    if (next != null) setText(String(next));
  }, [pushed]);

  const busy = props.busy === true;
  const ready = text.trim().length > 0;
  const live = busy || ready;

  const change = (value) => {
    setText(value);
    if (emit) emit('textChanged', { text: value });
  };
  const submit = () => {
    if (busy) { if (emit) emit('stopRequested', {}); return; }
    if (!ready) return;
    if (emit) emit('submitted', { text: text.trim() });
    setText('');
  };

  if (props.layout === 'stacked') {
    return <StackedComposer props={props} dark={ctx?.appearance === 'dark'} text={text} busy={busy} live={live}
      change={change} submit={submit} emit={emit} />;
  }

  return (
    <div data-composer style={{
      display: 'flex', alignItems: 'center', gap: 'var(--sp-1)', padding: 6,
      border: '1px solid var(--line)', borderRadius: 26,
      background: 'var(--surface)', boxShadow: 'var(--shadow-1)',
    }}>
      {props.attach === false ? <span style={{ width: 'var(--sp-2)' }} /> : (
        <IconButton
          name="plus" label="Add photo or file" size={22} box={40}
          onClick={emit ? () => emit('attachRequested', {}) : undefined}
        />
      )}
      <input
        type="text" value={text} aria-label={props.placeholder || 'Message'}
        placeholder={props.placeholder || 'Message'}
        onChange={(e) => change(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !busy) { e.preventDefault(); submit(); } }}
        style={{
          flex: '1 1 auto', minWidth: 0, height: 40, border: 0, outline: 0, background: 'transparent',
          font: 'inherit', fontSize: TURN_SIZE, color: 'var(--text)',
        }}
      />
      <button
        type="button" aria-label={busy ? 'Stop replying' : 'Send'} disabled={!live} onClick={submit}
        data-mode={busy ? 'stop' : 'send'}
        style={{
          ...resetButton, width: 40, height: 40, borderRadius: 20, flex: '0 0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: live ? 'var(--text)' : 'var(--surface-3)',
          color: live ? 'var(--surface)' : 'var(--text-3)',
          cursor: live ? 'pointer' : 'default',
        }}
      >
        {busy ? (
          <span aria-hidden style={{ width: 12, height: 12, borderRadius: 3, background: 'currentColor' }} />
        ) : <Glyph name="send" size={20} weight={2.4} />}
      </button>
    </div>
  );
}

/** `layout: "stacked"` — the field on its own row, then + on the left and mic + an ink send on the right. */
function StackedComposer({ props, dark, text, busy, live, change, submit, emit }) {
  const ink = dark ? '#FFFFFF' : '#0A0A0A';
  const line = dark ? '#3A3A3E' : '#E4E7EC';
  const round = (key, glyph, onClick, { solid = false, label } = {}) => (
    <button type="button" data-composer-button={key} aria-label={label} onClick={onClick} style={{
      ...resetButton, width: 32, height: 32, borderRadius: 16, boxSizing: 'border-box', flex: '0 0 auto',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: solid ? ink : 'transparent', border: `1px solid ${solid ? ink : line}`, color: ink,
      cursor: onClick ? 'pointer' : 'default',
    }}>
      {busy && solid
        ? <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, background: dark ? '#0A0A0A' : '#FFFFFF' }} />
        : inboxGlyph(glyph, { size: 16, color: solid ? (dark ? '#0A0A0A' : '#FFFFFF') : ink, stroke: solid ? 2 : 1.7 })}
    </button>
  );
  return (
    <div data-composer="stacked" style={{
      border: `1px solid ${dark ? '#2C2C2E' : '#E4E7EC'}`, borderRadius: 24, padding: '12px 10px 8px 16px',
      background: dark ? '#1C1C1E' : '#FFFFFF',
    }}>
      <input
        type="text" value={text} aria-label={props.placeholder || 'Message'}
        placeholder={props.placeholder || 'Message'}
        onChange={(e) => change(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !busy) { e.preventDefault(); submit(); } }}
        style={{
          width: '100%', boxSizing: 'border-box', border: 0, outline: 0, padding: 0, background: 'transparent',
          font: 'inherit', fontSize: 15, color: ink,
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, marginLeft: -6 }}>
        {props.attach === false ? null : round('attach', 'plus', emit ? () => emit('attachRequested', {}) : undefined, { label: 'Add photo or file' })}
        <span style={{ flex: 1 }} />
        {props.mic === true ? round('mic', 'mic', emit ? () => emit('micRequested', {}) : undefined, { label: 'Talk' }) : null}
        {round(busy ? 'stop' : 'send', 'send-up', live ? submit : undefined, { solid: true, label: busy ? 'Stop replying' : 'Send' })}
      </div>
    </div>
  );
}

function rowsOf(source, data) {
  const raw = source !== undefined ? source : data;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row) => row && typeof row === 'object' && row.key != null && String(row.label || '').trim())
    .map((row) => ({ ...row, key: String(row.key), label: String(row.label) }));
}

/**
 * NavList — a list of places to go, in the groups the data names.
 *
 * A group header is drawn wherever `group` changes from the row before, the
 * same way a thread draws its days. `searchable` filters by label on the
 * device; `actionLabel` adds one trailing action beside the search field.
 */
export function NavList({ props = {}, data, emit }) {
  const [query, setQuery] = useState('');
  const rows = rowsOf(props.items, data);
  const needle = query.trim().toLowerCase();
  const shown = needle ? rows.filter((r) => r.label.toLowerCase().includes(needle)) : rows;
  const selected = props.selected != null ? String(props.selected) : null;
  const searchable = props.searchable === true;
  const actionLabel = props.actionLabel ? String(props.actionLabel) : '';

  const groups = groupSplits(shown);
  const cells = [];
  shown.forEach((row, i) => {
    const { group, split } = groups[i];
    if (split) {
      cells.push(
        <div key={`g-${group}-${row.key}`} data-group={group} style={{
          padding: `${i ? 18 : 10}px var(--sp-3) var(--sp-1)`,
          fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--text-2)',
        }}>{group}</div>,
      );
    }
    const on = row.key === selected;
    cells.push(
      <button
        key={row.key} type="button" data-row={row.key} aria-current={on ? 'true' : undefined}
        onClick={emit ? () => emit('picked', { key: row.key, label: row.label }) : undefined}
        style={{
          ...resetButton, minHeight: 44, padding: '0 var(--sp-3)', borderRadius: 12,
          display: 'flex', alignItems: 'center', width: '100%',
          fontSize: TURN_SIZE, fontWeight: on ? 500 : 400, color: 'var(--text)',
          background: on ? 'var(--surface-3)' : 'transparent',
        }}
      >
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.label}
        </span>
      </button>,
    );
  });

  return (
    <div data-navlist style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', minWidth: 0 }}>
      {searchable || actionLabel ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', padding: '0 var(--sp-1) var(--sp-2)' }}>
          {searchable ? (
            <div style={{
              flex: '1 1 auto', minWidth: 0, height: 40, borderRadius: 12, padding: '0 var(--sp-3)',
              display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
              background: 'var(--surface-3)', color: 'var(--text-2)',
            }}>
              <Glyph name="search" size={18} />
              <input
                type="text" value={query} aria-label={props.placeholder || 'Search'}
                placeholder={props.placeholder || 'Search'}
                onChange={(e) => setQuery(e.target.value)}
                style={{
                  flex: '1 1 auto', minWidth: 0, border: 0, outline: 0, background: 'transparent',
                  font: 'inherit', fontSize: TURN_SIZE, color: 'var(--text)',
                }}
              />
            </div>
          ) : <span style={{ flex: '1 1 auto' }} />}
          {actionLabel ? (
            <IconButton
              name="compose" label={actionLabel}
              onClick={emit ? () => emit('actionRequested', {}) : undefined}
            />
          ) : null}
        </div>
      ) : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {cells.length ? cells : (
          <div data-navlist-empty style={{ padding: 'var(--sp-3)', fontSize: TURN_SIZE, color: 'var(--text-2)' }}>
            {needle ? 'No matches' : (props.emptyLabel ? String(props.emptyLabel) : '')}
          </div>
        )}
      </div>
    </div>
  );
}

/** Per row: its group label, and whether it differs from the last row that had one. */
function groupSplits(rows) {
  let last = null;
  return rows.map((row) => {
    const group = row.group ? String(row.group) : null;
    const split = Boolean(group) && group !== last;
    if (group) last = group;
    return { group, split };
  });
}

/**
 * ChatPage — the conversation surface: a header, a body, a pinned composer and
 * a side list that slides in over them.
 *
 * Children route by `region`: `composer` is pinned under the body, `drawer`
 * is the side list, everything else is the body. The header's two buttons are
 * the page's own — the list button opens the drawer, the other asks for a new
 * conversation — and both are also signals, so a blueprint can react to them.
 */
export function ChatPage({ props = {}, emit, slots, childItems = [], children }) {
  const [open, setOpen] = useState(false);
  const openSlot = slots?.openDrawer;
  const closeSlot = slots?.closeDrawer;
  useEffect(() => { if (openSlot) setOpen(true); }, [openSlot]);
  useEffect(() => { if (closeSlot) setOpen(false); }, [closeSlot]);

  const items = childItems.length ? childItems : null;
  const inRegion = (name) => (items ? items.filter((c) => c.region === name).map((c) => c.element) : []);
  const body = items
    ? items.filter((c) => c.region !== 'composer' && c.region !== 'drawer').map((c) => c.element)
    : children;
  const composer = inRegion('composer');
  const drawer = inRegion('drawer');
  const title = props.title ? String(props.title) : '';

  const menu = () => {
    setOpen(true);
    if (emit) emit('menuRequested', {});
  };
  const close = () => {
    setOpen(false);
    if (emit) emit('drawerClosed', {});
  };

  return (
    <div data-chat-page style={{
      position: 'relative', display: 'flex', flexDirection: 'column', minHeight: '100%',
      background: 'var(--surface)', color: 'var(--text)',
    }}>
      <div data-chat-header style={{
        position: 'sticky', top: 0, zIndex: 2, height: 52, padding: '0 var(--sp-2)',
        display: 'flex', alignItems: 'center',
        background: 'var(--surface)', borderBottom: '1px solid var(--line-soft)',
      }}>
        {drawer.length ? (
          <IconButton name="menu" label={props.menuLabel || 'Open chat list'} onClick={menu} />
        ) : <span style={{ width: 44 }} />}
        <div style={{
          flex: '1 1 auto', minWidth: 0, textAlign: 'center',
          fontSize: 'var(--t-lg)', fontWeight: 600,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{title}</div>
        {props.newAction === false ? <span style={{ width: 44 }} /> : (
          <IconButton
            name="compose" label={props.newLabel || 'New chat'}
            onClick={emit ? () => emit('newRequested', {}) : undefined}
          />
        )}
      </div>

      <div data-chat-body style={{
        flex: '1 1 auto', display: 'flex', flexDirection: 'column', minWidth: 0,
        padding: '20px 20px var(--sp-2)',
      }}>
        {body}
      </div>

      {composer.length ? (
        <div data-chat-composer style={{
          position: 'sticky', bottom: 0, zIndex: 2, padding: 'var(--sp-2) var(--sp-3) var(--sp-4)',
          background: 'var(--surface)',
        }}>
          {composer}
        </div>
      ) : null}

      {open && drawer.length ? (
        <div data-chat-drawer style={{ position: 'absolute', inset: 0, zIndex: 5, display: 'flex' }}>
          <div
            role="dialog" aria-label={props.menuLabel || 'Chat list'}
            style={{
              width: '84%', maxWidth: 326, boxSizing: 'border-box', padding: '20px var(--sp-3)',
              background: 'var(--surface)', borderRadius: '0 var(--r-lg) var(--r-lg) 0',
              boxShadow: 'var(--shadow-pop)', animation: 'viNativeSheetIn 0.18s ease-out',
            }}
          >
            {drawer}
          </div>
          <button
            type="button" aria-label="Close chat list" onClick={close}
            style={{ ...resetButton, flex: '1 1 auto', background: 'color-mix(in srgb, var(--text) 45%, transparent)' }}
          />
        </div>
      ) : null}
    </div>
  );
}
